import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useParams } from "react-router-dom"
import axios from "axios"
import Swal from "sweetalert2"

const URI_DOCS_ASSIGNED = process.env.REACT_APP_URI_DOCS_ASSIGNED_TO_CLIENT  // /docsAssignedToClient/
const URI_DOCS_TO_ASSIGN = process.env.REACT_APP_URI_DOCUMENTS_TO_ASSIGN     // /getDocumentsToAssign/

// ─── API ──────────────────────────────────────────────────────────────────────

const fetchAssignedDocs = (clientId) =>
    axios.get(URI_DOCS_ASSIGNED + clientId).then(res => res.data)

const fetchAvailableDocs = () =>
    axios.get(URI_DOCS_TO_ASSIGN).then(res => res.data)

// ─── HOOK ─────────────────────────────────────────────────────────────────────

export function useClientDocuments() {
    const { id: clientId } = useParams()
    const queryClient = useQueryClient()

    const [showModal, setShowModal] = useState(false)

    // ─── QUERIES ──────────────────────────────────────────────────────────────

    const { data: assignedDocs = [], isLoading: isLoadingAssigned } = useQuery({
        queryKey: ["docsAssignedToClient", clientId],
        queryFn: () => fetchAssignedDocs(clientId),
        enabled: !!clientId,
    })

    const { data: availableDocs = [], isLoading: isLoadingAvailable } = useQuery({
        queryKey: ["documentsToAssign"],
        queryFn: fetchAvailableDocs,
        enabled: showModal, // solo carga cuando se abre el modal
    })

    // ─── MUTATIONS ────────────────────────────────────────────────────────────

    const { mutate: assignDoc, isPending: isAssigning } = useMutation({
        mutationFn: ({ entityId, documents }) =>
            axios.post(URI_DOCS_ASSIGNED, { clientId, entityId, documents }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["docsAssignedToClient", clientId], refetchType: "all" })
            Swal.fire({
                icon: "success",
                title: "Documents assigned",
                text: "The documents were successfully assigned to the entity.",
                timer: 2500,
                showConfirmButton: false,
            })
        },
        onError: (error) => {
            console.error("Error assigning documents:", error)
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error?.response?.data?.message || "An error occurred while assigning documents.",
            })
        }
    })

    const { mutate: removeDoc, isPending: isRemoving } = useMutation({
        mutationFn: (assignmentId) =>
            axios.delete(URI_DOCS_ASSIGNED + assignmentId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["docsAssignedToClient", clientId], refetchType: "all" })
        },
    })

    // ─── ASIGNAR SELECCIONADOS (llamado desde el modal al dar "Agregar") ──────

    const addSelectedDocs = (docs, entityId) => {
        if (!entityId) return

        const documents = docs.map((doc) => ({
            documentId: doc._id,
            documentCollection: doc.collection || doc.documentCollection,
        }))

        assignDoc({ entityId, documents })
    }

    const isDocAssigned = (docId, entityId) =>
        assignedDocs.some((assignedDoc) => {
            const sameEntity = assignedDoc.entityAssignId?.toString() === entityId?.toString()
            const sameDocument = assignedDoc.documentId?.toString() === docId.toString()
            return sameEntity && sameDocument
        })

    return {
        assignedDocs,
        availableDocs,
        isLoadingAssigned,
        isLoadingAvailable,
        isAssigning,
        isRemoving,
        showModal,
        setShowModal,
        addSelectedDocs,
        isDocAssigned,
        removeDoc,
    }
}
